import { useEffect, useRef, useState, useCallback } from "react";
import { fetchAllQuizData } from "./fetchAllQuizData";
import { loadExistingInsights } from "./loadExistingInsights";
import { useGenerateAllInsights } from "./useGenerateAllInsights";

export const useProfileInsights = ({ userId, displayName }) => {
  const [profileOverview, setProfileOverview] = useState(null);
  const [quizInsights, setQuizInsights] = useState({});
  const [allQuizData, setAllQuizData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [insightsInitialized, setInsightsInitialized] = useState(false);

  const initializingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const generateAllInsights = useGenerateAllInsights({
    mountedRef,
    getters: {
      getAllQuizData: () => allQuizData,
      getProfileOverview: () => profileOverview,
      getQuizInsights: () => quizInsights,
    },
    setters: {
      setLoading,
      setError,
      setProfileOverview,
      setQuizInsights,
    },
    context: { displayName, userId },
  });

  const regenerateInsights = useCallback(async () => {
    if (!mountedRef.current || !allQuizData || allQuizData.length === 0) {
      return;
    }
    setIsRegenerating(true);
    setError(null);
    setProfileOverview(null);
    setQuizInsights({});
    setInsightsInitialized(false);
    try {
      await generateAllInsights(allQuizData, true);
    } catch (err) {
      setError("Failed to regenerate insights: " + err.message);
    } finally {
      setIsRegenerating(false);
    }
  }, [allQuizData, generateAllInsights]);

  useEffect(() => {
    const initializeInsights = async () => {
      if (initializingRef.current) return;
      if (insightsInitialized) return;
      if (!mountedRef.current) return;
      if (!userId) return;

      initializingRef.current = true;
      try {
        await import("../../services/firebase.config").then((m) => m.auth);
        if (!mountedRef.current) return;

        const quizData = await fetchAllQuizData(userId);
        if (!mountedRef.current) return;

        setAllQuizData(quizData);
        if (quizData.length === 0) return;

        const existingInsights = await loadExistingInsights(userId, quizData);
        if (!mountedRef.current) return;

        const hasAllIndividualInsights =
          Object.keys(existingInsights.individual).length === quizData.length;
        const hasOverview = !!existingInsights.overview;

        setProfileOverview(existingInsights.overview);
        setQuizInsights(existingInsights.individual);

        if (!hasOverview || !hasAllIndividualInsights) {
          if (!mountedRef.current) return;
          await generateAllInsights(quizData);
        }

        setInsightsInitialized(true);
      } catch (err) {
        if (mountedRef.current) {
          setError("Failed to initialize insights: " + err.message);
        }
      } finally {
        initializingRef.current = false;
      }
    };

    initializeInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, insightsInitialized]);

  return {
    state: {
      profileOverview,
      quizInsights,
      allQuizData,
      loading,
      error,
      isRegenerating,
      insightsInitialized,
    },
    actions: {
      regenerateInsights,
    },
    refs: { mountedRef },
  };
};
